import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Check, Clock, Loader2, X } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useToast } from '../ui/use-toast';
import { scheduleEventsService } from '../../services/schedule-events';
import type { ScheduleAttendee } from '../../types/schedule-event';

interface ScheduleAttendeesDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  gymSlug: string;
  eventId: string | null;
  occurrenceDate: string | null;
  /** Called after a staff confirm/remove so the panel can refresh counts. */
  onChanged?: () => void;
}

/**
 * Attendees of one occurrence. For non-staff the backend only reveals people the
 * viewer follows (plus themself); the rest are an anonymous count. Staff who
 * manage the gym see everyone with status and can confirm/remove registrations.
 */
export function ScheduleAttendeesDrawer({
  open,
  onOpenChange,
  gymSlug,
  eventId,
  occurrenceDate,
  onChanged,
}: ScheduleAttendeesDrawerProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [canManage, setCanManage] = useState(false);
  const [users, setUsers] = useState<ScheduleAttendee[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !eventId || !occurrenceDate) return;
    let cancelled = false;
    setLoading(true);
    scheduleEventsService
      .attendees(gymSlug, eventId, occurrenceDate)
      .then(result => {
        if (cancelled) return;
        setTotalCount(result.total_count);
        setCanManage(result.can_manage);
        setUsers(result.users);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, gymSlug, eventId, occurrenceDate]);

  const handleConfirm = async (attendee: ScheduleAttendee) => {
    if (!attendee.registration_id) return;
    setBusyId(attendee.registration_id);
    try {
      await scheduleEventsService.confirmRegistration(attendee.registration_id);
      setUsers(prev => prev.map(u => (u.id === attendee.id ? { ...u, status: 'confirmed' } : u)));
      onChanged?.();
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : t('errors.unexpectedError'), variant: 'destructive' });
    } finally {
      setBusyId(null);
    }
  };

  const handleRemove = async (attendee: ScheduleAttendee) => {
    if (!attendee.registration_id || !eventId) return;
    setBusyId(attendee.registration_id);
    try {
      await scheduleEventsService.removeRegistration(gymSlug, eventId, attendee.registration_id);
      setUsers(prev => prev.filter(u => u.id !== attendee.id));
      setTotalCount(c => Math.max(0, c - 1));
      onChanged?.();
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : t('errors.unexpectedError'), variant: 'destructive' });
    } finally {
      setBusyId(null);
    }
  };

  const hiddenCount = totalCount - users.length;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[80vh] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{t('schedule.attendees.title', { count: totalCount })}</SheetTitle>
        </SheetHeader>
        <div className="mt-4 space-y-2">
          {loading ? (
            <div className="text-sm text-muted-foreground py-6 text-center">…</div>
          ) : totalCount === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center">
              {t('schedule.attendees.empty')}
            </div>
          ) : (
            <>
              {users.map(attendee => (
                <div
                  key={attendee.id}
                  className="flex items-center gap-3 p-2 rounded-md hover:bg-muted transition-colors"
                >
                  <Link to={`/users/${attendee.slug}`} className="flex items-center gap-3 flex-1 min-w-0">
                    <Avatar className="h-9 w-9">
                      {attendee.avatar_small_url ? (
                        <AvatarImage src={attendee.avatar_small_url} alt={attendee.username ?? ''} />
                      ) : null}
                      <AvatarFallback className="text-xs">
                        {(attendee.username?.charAt(0) ?? '?').toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{attendee.username ?? '—'}</div>
                    </div>
                  </Link>

                  {attendee.status === 'pending' && (
                    <Badge variant="secondary" className="gap-1 shrink-0">
                      <Clock className="h-3 w-3" />
                      {t('schedule.pending')}
                    </Badge>
                  )}

                  {canManage && attendee.registration_id && (
                    <div className="flex items-center gap-1 shrink-0">
                      {attendee.status === 'pending' && (
                        <Button
                          size="sm"
                          className="h-8 gap-1.5"
                          disabled={busyId === attendee.registration_id}
                          onClick={() => handleConfirm(attendee)}
                        >
                          {busyId === attendee.registration_id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Check className="h-4 w-4" />
                          )}
                          {t('schedule.confirm')}
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        disabled={busyId === attendee.registration_id}
                        onClick={() => handleRemove(attendee)}
                        aria-label={t('schedule.remove')}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              ))}
              {hiddenCount > 0 && (
                <p className="text-sm text-muted-foreground py-2 text-center">
                  {t('schedule.attendees.others', { count: hiddenCount })}
                </p>
              )}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
